// گرافِ صفحه‌به‌صفحه‌ی سفرِ کاربر — منطقِ خالص، بدونِ DOM و بدونِ کتابخانه.
//
// ═══ چرا این، وقتی سنکی هست ═══
//
// سنکیِ `journeyReport.buildFlow` ستون‌هایش «پله»اند: اولین صفحه، دومین،
// سومین. آن نمودار جوابِ «کاربر از پله‌ی اول به کجا می‌رود» را می‌دهد، ولی
// یک صفحه در آن **چند گره** است — `/discover` در پله‌ی دوم و در پله‌ی چهارم
// دو گرهِ جدا هستند و هیچ‌جا نمی‌شود پرسید «روی هم، چند نفر وارد کشف شدند».
//
// این‌جا برعکس است: هر آدرسِ صفحه **یک** گره است و یال‌ها جهت‌دارند. حلقه
// (کشف → رویداد → کشف) این‌جا مشکل نیست، خودِ خبر است.
//
// ═══ چرا گرهِ «خروج» ندارد ═══
//
// در سنکی ریزش باید یک گره باشد وگرنه جریان بی‌صدا کم می‌شود. این‌جا
// نمی‌شود: یک گرهِ خروجِ مشترک که همه به آن وصل‌اند، وسطِ گراف یک ستاره‌ی
// پر از یال می‌سازد و بقیه‌ی شکل را می‌خورد. پس خروج **رنگِ خودِ گره** است —
// سبز یعنی هیچ‌کس از این صفحه بیرون نرفت، قرمز یعنی همه، و وسطش خنثی.
// همان عدد، بدونِ یک یال اضافه به ازای هر صفحه.
//
// گرهِ «ورود» ولی می‌ماند و مصنوعی نیست: «نشست از کدام صفحه شروع شد» یک
// یالِ واقعی است و بدونش صفحه‌های شروع، ورودیِ صفر نشان می‌دادند.
//
// خروجی مختصات و عدد است؛ کشیدنش کارِ اپ است — همان تقسیمِ کاری که
// `sankey.ts` دارد، تا `npm test` بدونِ مرورگر بیازمایدش.
import type { JourneyData, JourneySession, JourneyStep } from './journeyReport.ts';

/* ─────────────────────────── مدل ─────────────────────────── */

/**
 * شناسه‌ی گرهِ «ورود».
 *
 * با نامِ هیچ صفحه‌ای قاطی نمی‌شود چون نامِ صفحه همیشه با `/` شروع می‌شود —
 * هر دو ردیابِ مسیر (`apps/web/src/app/routes.tsx` و
 * `apps/mobile/app/_layout.tsx`) مسیرِ کامل می‌فرستند.
 */
export const ENTRY = 'entry';

export interface GraphNode {
  /** نامِ صفحه، یا `ENTRY`. */
  id: string;
  /** چند بار این صفحه باز شد — یعنی مجموعِ یال‌های ورودی. */
  inbound: number;
  /** چند بار از این صفحه به جایی رفتند. */
  outbound: number;
  /** چند نشست روی این صفحه تمام شد. */
  exits: number;
  /**
   * `exits / inbound` — بینِ ۰ و ۱، و رنگِ گره از همین می‌آید.
   *
   * `null` یعنی **سنجیدنی نیست** و نه صفر: گرهِ «ورود» صفحه نیست، پس
   * کسی نمی‌تواند از آن «خارج» شود. صفر گذاشتن یعنی سبزِ پررنگ — یعنی
   * ادعای «این‌جا هیچ‌کس رها نکرد» درباره‌ی چیزی که اصلاً صفحه نیست.
   */
  exitRate: number | null;
  /** در چند نشستِ متمایز دیده شد. */
  sessions: number;
  /**
   * «این صفحه معمولاً چندمین صفحه‌ی سفر است» — میانگینِ شماره‌ی پله‌اش.
   * برای «ورود» صفر. عددی واقعی است و در کارتِ شناور نوشته می‌شود.
   */
  meanStep: number;
  /**
   * ستونِ گره. «ورود» صفر است و صفحه‌ها از یک به بالا، به ترتیبِ
   * `meanStep` — یعنی چپ اولِ سفر است و راست تهِ سفر.
   *
   * ⚠ ستون **رتبه**ی `meanStep` است و نه خودش، و ستون‌ها هم‌جمعیت‌اند.
   * دو نسخه‌ی قبلی هر دو روی دادهٔ واقعی فرو ریختند:
   *   • «فاصله از ورود با BFS» — تقریباً هر صفحه‌ای می‌تواند اولین صفحه‌ی
   *     یک نشست باشد (لینکِ مستقیم، رفرش، دیپ‌لینک)، پس همه فاصله‌ی یک
   *     داشتند و کلِ گراف یک ستونِ عمودی شد.
   *   • «میانگینِ پله، گرد شده» — در اپی که سفرهایش کوتاه است میانگینِ
   *     همه‌ی صفحه‌ها نزدیکِ هم می‌افتد و باز هفده گره در یک ستون نشستند.
   * رتبه‌بندی هر دو را می‌بندد و ترتیبِ چپ‌به‌راست را هم نگه می‌دارد.
   */
  depth: number;
}

export interface GraphEdge {
  from: string;
  to: string;
  /** چند بار این گذر اتفاق افتاد. */
  value: number;
  /**
   * یالی که به ستونِ خودش یا به عقب برمی‌گردد — یعنی کاربر به صفحه‌ای رفته
   * که معمولاً **زودتر** از این یکی در سفر دیده می‌شود. خط‌چین کشیده
   * می‌شود، چون این خودش خبر است: برگشتن، نه پیش رفتن.
   */
  back: boolean;
}

export interface JourneyGraphResult {
  nodes: GraphNode[];
  edges: GraphEdge[];
  /** یال‌هایی که با `minEdge` کنار رفتند — تا نمایش بتواند صریح بگویدشان. */
  hiddenEdges: { count: number; transitions: number };
}

export interface JourneyGraphOptions {
  /**
   * نازک‌ترین یالی که کشیده می‌شود. پیش‌فرض ۱ یعنی «همه».
   *
   * ⚠ هرچه کنار برود در `hiddenEdges` شمرده می‌شود و رابط می‌نویسدش. یالِ
   * بی‌صدا حذف‌شده یعنی گرافی که کم می‌شمارد، و آن از گرافِ شلوغ بدتر است.
   */
  minEdge?: number;
  /**
   * سقفِ گره در هر ستون. ستون‌ها به همین اندازه هم‌جمعیت می‌شوند و
   * تعدادشان از همین درمی‌آید.
   */
  maxPerColumn?: number;
  /** بیشترین تعدادِ ستونِ صفحه‌ها — بی این، سی صفحه سی ستون می‌شد. */
  maxColumns?: number;
  /**
   * فقط نشست‌های یک سطح — **همان گزینه و همان معنیِ**
   * `JourneyOptions.surface` (WHOSUP-242).
   *
   * ⚠ داشتنش اختیاری نیست: این گراف و سنکیِ `journeyReport` دو تبِ یک
   * کارت‌اند و بالای سربرگ یک فیلترِ سطحِ مشترک دارند. اگر یکی فیلتر شود و
   * آن یکی نه، دو تبِ کنارِ هم دو عدد می‌دهند و هیچ‌کدام نمی‌گوید چرا.
   */
  surface?: JourneySession['surface'] | 'all';
}

/* ─────────────────────────── ساختِ گراف ─────────────────────────── */

export function buildJourneyGraph(
  data: JourneyData, opts: JourneyGraphOptions = {},
): JourneyGraphResult {
  const minEdge = Math.max(1, opts.minEdge ?? 1);
  const surface = opts.surface ?? 'all';
  // قرینه‌ی دقیقِ فیلترِ `buildJourneyReport`: سطح رویِ **نشست** است و پله
  // ستونِ سطح ندارد، پس همان مجموعه‌ی شناسه فیلترِ پله‌ها هم هست.
  const all: JourneySession[] = data.sessions ?? [];
  const sessions = surface === 'all' ? all : all.filter((s) => s.surface === surface);
  const keep = new Set(sessions.map((s) => s.session_id));
  const steps: JourneyStep[] = surface === 'all'
    ? (data.steps ?? [])
    : (data.steps ?? []).filter((s) => keep.has(s.session_id));

  // پله‌های هر نشست، به ترتیب.
  const bySession = new Map<string, JourneyStep[]>();
  for (const s of steps) {
    const arr = bySession.get(s.session_id);
    if (arr) arr.push(s); else bySession.set(s.session_id, [s]);
  }
  for (const arr of bySession.values()) arr.sort((a, b) => a.step - b.step);

  const edgeCount = new Map<string, number>();
  const bump = (from: string, to: string): void => {
    const key = `${from}>${to}`;
    edgeCount.set(key, (edgeCount.get(key) ?? 0) + 1);
  };

  const seenIn = new Map<string, Set<string>>();
  const views = new Map<string, number>();
  const outs = new Map<string, number>();
  /** مجموعِ شماره‌ی پله‌ها، برای میانگینی که ستونِ گره از آن می‌آید. */
  const stepSum = new Map<string, number>();

  for (const [sid, arr] of bySession) {
    // نشست از جایی شروع شده — و آن یالِ واقعیِ «ورود → اولین صفحه» است.
    bump(ENTRY, arr[0].screen);
    for (let i = 0; i < arr.length; i += 1) {
      const scr = arr[i].screen;
      views.set(scr, (views.get(scr) ?? 0) + 1);
      // ⚠ جای **واقعیِ** پله در سفر و نه `i`: پله‌ها از یک شروع می‌شوند و
      // ممکن است شماره‌شان پیوسته نباشد.
      stepSum.set(scr, (stepSum.get(scr) ?? 0) + arr[i].step);
      let set = seenIn.get(scr);
      if (!set) { set = new Set(); seenIn.set(scr, set); }
      set.add(sid);
      if (i + 1 < arr.length) {
        bump(scr, arr[i + 1].screen);
        outs.set(scr, (outs.get(scr) ?? 0) + 1);
      }
    }
  }

  // خروج از `exit_screen`ِ خودِ نشست می‌آید و نه از «پله‌ی آخر»: نشستی که
  // هنوز باز است پله‌ی آخر دارد ولی خروج ندارد، و شمردنش یعنی هر صفحه‌ای
  // که کسی همین حالا رویش است، قرمزتر از واقعیت دیده شود.
  const exits = new Map<string, number>();
  for (const s of sessions) {
    if (s.exit_screen) exits.set(s.exit_screen, (exits.get(s.exit_screen) ?? 0) + 1);
  }

  const kept: GraphEdge[] = [];
  let hiddenCount = 0;
  let hiddenTransitions = 0;
  for (const [key, value] of edgeCount) {
    if (value < minEdge) { hiddenCount += 1; hiddenTransitions += value; continue; }
    const [from, to] = key.split('>');
    kept.push({ from, to, value, back: false });
  }

  // گره‌ها: هر صفحه‌ای که پله‌ای دارد، به‌علاوه‌ی «ورود».
  //
  // نشستِ بی‌پله (فقط یک رویدادِ غیرِ صفحه) گرافی ندارد؛ گرهِ ورودِ تنها
  // یعنی نموداری که چیزی نمی‌گوید، پس اصلاً گرافی برنمی‌گردد.
  const ids = [...views.keys()];
  if (!ids.length) return { nodes: [], edges: [], hiddenEdges: { count: 0, transitions: 0 } };

  const screens: GraphNode[] = ids.map((id): GraphNode => {
    const inbound = views.get(id) ?? 0;
    return {
      id,
      inbound,
      outbound: outs.get(id) ?? 0,
      exits: exits.get(id) ?? 0,
      exitRate: inbound ? Math.min(1, (exits.get(id) ?? 0) / inbound) : null,
      sessions: seenIn.get(id)?.size ?? 0,
      meanStep: (stepSum.get(id) ?? 0) / Math.max(inbound, 1),
      depth: 1,
    };
  });

  // ستون‌ها: به ترتیبِ «معمولاً چندمین صفحه‌ی سفر»، هم‌جمعیت. چرایش سرِ
  // `GraphNode.depth`.
  const perCol = Math.max(1, opts.maxPerColumn ?? 5);
  const cols = Math.min(
    Math.max(1, opts.maxColumns ?? 5),
    Math.max(1, Math.ceil(screens.length / perCol)),
  );
  const size = Math.ceil(screens.length / cols);
  screens
    .sort((a, b) => a.meanStep - b.meanStep || b.inbound - a.inbound || (a.id < b.id ? -1 : 1))
    .forEach((n, i) => { n.depth = 1 + Math.floor(i / size); });

  const nodes: GraphNode[] = [
    {
      id: ENTRY,
      inbound: 0,
      outbound: bySession.size,
      exits: 0,
      exitRate: null,
      sessions: bySession.size,
      meanStep: 0,
      depth: 0,
    },
    ...screens,
  ].sort((a, b) => a.depth - b.depth || b.inbound - a.inbound || (a.id < b.id ? -1 : 1));

  const byId = new Map(nodes.map((n) => [n.id, n]));
  for (const e of kept) {
    const a = byId.get(e.from);
    const b = byId.get(e.to);
    e.back = !a || !b || b.depth <= a.depth;
  }
  kept.sort((a, b) => b.value - a.value || (a.from < b.from ? -1 : 1));

  return {
    nodes,
    edges: kept,
    hiddenEdges: { count: hiddenCount, transitions: hiddenTransitions },
  };
}

/* ─────────────────────────── چیدمان ─────────────────────────── */

export interface LaidOutGraphNode extends GraphNode {
  x: number;
  y: number;
  /** شعاع. سطحِ دایره متناسبِ `inbound` است، نه شعاعش — پایین‌تر چرا. */
  r: number;
}

/** یک سرِ پیکان: کجا بنشیند و چند درجه بچرخد تا بر منحنی مماس باشد. */
export interface EdgeArrow {
  x: number;
  y: number;
  /** درجه، برای `rotate()`ِ SVG. */
  deg: number;
}

export interface LaidOutGraphEdge extends GraphEdge {
  /** مسیرِ SVG آماده — از لبه‌ی دایره‌ی مبدأ تا لبه‌ی دایره‌ی مقصد. */
  path: string;
  thickness: number;
  /**
   * سرِ پیکان، **وسطِ** منحنی و نه تهش.
   *
   * ⚠ اولین نسخه پیکان را با `marker-end` تهِ یال می‌گذاشت و عملاً دیده
   * نمی‌شد: ده یال به یک گره می‌رسند، پس ده سرِ پیکان روی هم و نیمه‌زیرِ
   * خودِ دایره جمع می‌شدند. وسطِ منحنی هیچ‌کدام روی دیگری نمی‌افتد — و چون
   * یالِ رفت و یالِ برگشتِ یک جفت به دو سمت خم می‌شوند، دو پیکان دو طرفِ
   * هم می‌نشینند و جهت یک‌نگاهی خوانده می‌شود.
   */
  arrow: EdgeArrow;
}

export interface GraphLayout {
  nodes: LaidOutGraphNode[];
  edges: LaidOutGraphEdge[];
  width: number;
  height: number;
}

export interface GraphLayoutOptions {
  width?: number;
  height?: number;
  minR?: number;
  maxR?: number;
  /** ضخامتِ نازک‌ترین و کلفت‌ترین یال. */
  minEdgeWidth?: number;
  maxEdgeWidth?: number;
  /** فاصله‌ی تهِ یال تا لبه‌ی دایره‌ی مقصد. */
  arrowGap?: number;
  /**
   * جای دستیِ گره‌ها، وقتی کاربر با موس جابه‌جایشان کرده — `id` به مختصات.
   *
   * ⚠ **بعد از** چیدنِ خودکار اعمال می‌شود و نه به‌جای آن: گرهی که در نقشه
   * نیست سرِ جای محاسبه‌شده‌اش می‌ماند، پس یک چیدمانِ ذخیره‌شده‌ی قدیمی که
   * صفحه‌ی تازه را نمی‌شناسد، آن صفحه را گم نمی‌کند.
   *
   * ⚠ یال‌ها **بعد از** این اعمال مسیر می‌گیرند، وگرنه گرهِ جابه‌جاشده
   * یال‌هایش سرِ جای قبلی جا می‌ماندند.
   */
  positions?: Record<string, { x: number; y: number }>;
  /**
   * کادری که گرهِ دستی‌جابه‌جاشده از آن بیرون نمی‌رود. پیش‌فرض: خودِ کادرِ
   * چیدمان.
   *
   * ⚠ جدا از `width`/`height` است چون آن دو **قلمروِ چیدنِ خودکار**اند و
   * عمداً از ناحیه‌ی قابلِ دیدن باریک‌ترند (حاشیه برای برچسبِ زیرِ گره).
   * با یکی گرفتنشان، گرهِ ستونِ آخر عملاً قفل می‌شد: آزمایش نشان داد که
   * ۲۰۰ پیکسل کشیدن به راست، فقط ۱۴ پیکسل جابه‌جایش می‌کرد.
   */
  bounds?: { x0: number; y0: number; x1: number; y1: number };
}

const LAYOUT_DEFAULTS = {
  width: 880, height: 460, minR: 7, maxR: 30,
  minEdgeWidth: 1, maxEdgeWidth: 9, arrowGap: 4,
};

/**
 * گره‌ها را ستون‌به‌ستون (بر اساسِ `depth`) جا می‌گذارد و یال‌ها را به مسیرِ
 * SVG تبدیل می‌کند.
 *
 * ⚠ شعاع از **ریشه‌ی دوم** می‌آید و نه خطی: چشم سطحِ دایره را می‌خواند، پس
 * شعاعِ خطی صفحه‌ای که ده برابرِ بغلی‌اش ورودی دارد را صد برابر بزرگ‌تر
 * نشان می‌دهد. با ریشه‌ی دوم، نسبتِ سطح‌ها همان نسبتِ عددهاست.
 *
 * ⚠ ترتیبِ گره‌ها در هر ستون یک پاسِ «مرکزِ ثقل» می‌خورد: هر گره نزدیکِ
 * میانگینِ جای گره‌هایی می‌نشیند که یالِ ورودی از ستونِ قبل به آن دارند.
 * بدونش یال‌ها بی‌دلیل هم را قطع می‌کردند و گراف هرچه گره بیشتر داشت
 * ناخواناتر می‌شد.
 */
export function layoutJourneyGraph(
  nodes: GraphNode[], edges: GraphEdge[], opts: GraphLayoutOptions = {},
): GraphLayout {
  const o = { ...LAYOUT_DEFAULTS, ...opts };
  if (!nodes.length) return { nodes: [], edges: [], width: o.width, height: o.height };

  /** ورودی برای صفحه‌ها، و برای «ورود» تعدادِ نشست‌هایی که از آن رد شدند. */
  const sizeOf = (n: GraphNode): number => Math.max(n.inbound, n.outbound);
  const maxSize = Math.max(1, ...nodes.map(sizeOf));

  const depths = [...new Set(nodes.map((n) => n.depth))].sort((a, b) => a - b);
  const cols = depths.map((d) => nodes.filter((n) => n.depth === d));

  // ستونِ اول: بزرگ‌ترین بالا. بقیه: مرکزِ ثقلِ ستونِ قبل.
  const rank = new Map<string, number>();
  cols.forEach((col, ci) => {
    if (ci === 0) {
      col.sort((a, b) => sizeOf(b) - sizeOf(a) || (a.id < b.id ? -1 : 1));
    } else {
      const bary = new Map<string, number>();
      for (const n of col) {
        const froms = edges
          .filter((e) => e.to === n.id && rank.has(e.from))
          .map((e) => rank.get(e.from)!);
        bary.set(n.id, froms.length
          ? froms.reduce((s, x) => s + x, 0) / froms.length
          : Number.POSITIVE_INFINITY);
      }
      col.sort((a, b) => (bary.get(a.id)! - bary.get(b.id)!)
        || sizeOf(b) - sizeOf(a) || (a.id < b.id ? -1 : 1));
    }
    col.forEach((n, i) => rank.set(n.id, i));
  });

  const padX = o.maxR + 6;
  const spanX = cols.length > 1 ? (o.width - padX * 2) / (cols.length - 1) : 0;

  // ⚠ فاصله‌ی عمودی در همه‌ی ستون‌ها یکی است و ستونِ کم‌جمعیت **وسط‌چین**
  // می‌شود، نه پخش‌شده در تمامِ ارتفاع. با پخش کردن، ستونی که دو گره دارد
  // آن‌ها را به بالا و پایینِ کادر می‌چسباند و یال‌هایش بی‌دلیل قطری و بلند
  // می‌شدند — گراف شلوغ‌تر به نظر می‌رسید بی آنکه چیزی بیشتر بگوید.
  const tallest = Math.max(...cols.map((c) => c.length));
  const slot = o.height / tallest;

  const placed = new Map<string, LaidOutGraphNode>();
  cols.forEach((col, ci) => {
    const x = cols.length > 1 ? padX + ci * spanX : o.width / 2;
    const top = (o.height - slot * col.length) / 2;
    col.forEach((n, i) => {
      placed.set(n.id, {
        ...n,
        x,
        y: top + slot * (i + 0.5),
        r: o.minR + (o.maxR - o.minR) * Math.sqrt(sizeOf(n) / maxSize),
      });
    });
  });

  // جای دستیِ کاربر، روی چیدنِ خودکار. داخلِ کادر نگه داشته می‌شود تا یک
  // مقدارِ ذخیره‌شده‌ی خراب نتواند گره را از صفحه بیندازد بیرون.
  if (o.positions) {
    const box = o.bounds ?? { x0: 0, y0: 0, x1: o.width, y1: o.height };
    for (const [id, p] of Object.entries(o.positions)) {
      const n = placed.get(id);
      if (!n || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
      n.x = Math.min(box.x1 - n.r, Math.max(box.x0 + n.r, p.x));
      n.y = Math.min(box.y1 - n.r, Math.max(box.y0 + n.r, p.y));
    }
  }

  const maxEdge = Math.max(1, ...edges.map((e) => e.value));
  const laid: LaidOutGraphEdge[] = [];
  for (const e of edges) {
    const a = placed.get(e.from);
    const b = placed.get(e.to);
    // گره‌ی حذف‌شده یال را با خودش می‌برد و نمی‌ترکاند: `minEdge` می‌تواند
    // یک گره را کاملاً بی‌یال کند و آن هنوز گرهِ درستی است.
    if (!a || !b) continue;
    laid.push({
      ...e,
      thickness: o.minEdgeWidth
        + (o.maxEdgeWidth - o.minEdgeWidth) * Math.sqrt(e.value / maxEdge),
      ...(a.id === b.id ? selfLoop(a, o.arrowGap) : link(a, b, o.arrowGap)),
    });
  }

  return { nodes: [...placed.values()], edges: laid, width: o.width, height: o.height };
}

/**
 * منحنی بینِ دو دایره — از لبه تا لبه، همیشه کمی خم.
 *
 * ⚠ سمتِ خم از **مقایسه‌ی نامِ دو سرِ یال** می‌آید و نه از جهتش. دلیلش یک
 * چیز است: `الف→ب` و `ب→الف` هر دو هستند و اگر هر دو مستقیم (یا هر دو به
 * یک سمت خم) باشند دقیقاً روی هم می‌افتند و آدم یک یال می‌بیند به‌جای دو
 * تا. با این قاعده، دو سرِ یک جفت همیشه دو طرفِ هم می‌افتند.
 *
 * ⚠ یالِ هم‌ستون بیشتر خم می‌شود: خطِ مستقیم بینِ دو گرهِ یک ستون از روی
 * هر گره‌ای که بینشان است رد می‌شود.
 */
function link(
  a: LaidOutGraphNode, b: LaidOutGraphNode, gap: number,
): { path: string; arrow: EdgeArrow } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const x0 = a.x + ux * a.r;
  const y0 = a.y + uy * a.r;
  const x1 = b.x - ux * (b.r + gap);
  const y1 = b.y - uy * (b.r + gap);

  const side = a.id < b.id ? 1 : -1;
  const sameCol = a.depth === b.depth;
  const bow = side * Math.min(110, len * (sameCol ? 0.38 : 0.16));
  // عمود بر خطِ وصل.
  const cx = (x0 + x1) / 2 - uy * bow;
  const cy = (y0 + y1) / 2 + ux * bow;
  return {
    path: `M${r2(x0)},${r2(y0)} Q${r2(cx)},${r2(cy)} ${r2(x1)},${r2(y1)}`,
    // ⚠ ۰٫۶ و نه ۰٫۵: کمی نزدیک‌تر به مقصد، چون همان‌جاست که چشم دنبالِ
    // جهت می‌گردد — ولی هنوز به‌قدرِ کافی دور که رویِ دایره نیفتد.
    arrow: quadArrow(x0, y0, cx, cy, x1, y1, 0.6),
  };
}

/** حلقه‌ی روی خودِ گره — صفحه‌ای که کاربر دوباره بازش می‌کند. */
function selfLoop(a: LaidOutGraphNode, gap: number): { path: string; arrow: EdgeArrow } {
  const r = a.r;
  const x0 = a.x - r * 0.6;
  const y0 = a.y - r * 0.8;
  const x1 = a.x + r * 0.6;
  const y1 = a.y - r * 0.8 - gap * 0.2;
  const lift = r + 22;
  const c1x = a.x - lift;
  const c1y = a.y - lift;
  const c2x = a.x + lift;
  const c2y = a.y - lift;
  return {
    path: `M${r2(x0)},${r2(y0)} C${r2(c1x)},${r2(c1y)} `
      + `${r2(c2x)},${r2(c2y)} ${r2(x1)},${r2(y1)}`,
    arrow: cubicArrow(x0, y0, c1x, c1y, c2x, c2y, x1, y1, 0.62),
  };
}

/* ── سرِ پیکان: نقطه و مماس روی منحنی ── */

/** منحنیِ درجه‌دو (`Q`) در پارامترِ `t`، با زاویه‌ی مماسش. */
function quadArrow(
  x0: number, y0: number, cx: number, cy: number, x1: number, y1: number, t: number,
): EdgeArrow {
  const u = 1 - t;
  const x = u * u * x0 + 2 * u * t * cx + t * t * x1;
  const y = u * u * y0 + 2 * u * t * cy + t * t * y1;
  const tx = 2 * u * (cx - x0) + 2 * t * (x1 - cx);
  const ty = 2 * u * (cy - y0) + 2 * t * (y1 - cy);
  return { x: r2(x), y: r2(y), deg: deg(tx, ty) };
}

/** همان، برای منحنیِ درجه‌سه (`C`) — یعنی حلقه‌ی روی خودِ گره. */
function cubicArrow(
  x0: number, y0: number, c1x: number, c1y: number,
  c2x: number, c2y: number, x1: number, y1: number, t: number,
): EdgeArrow {
  const u = 1 - t;
  const x = u * u * u * x0 + 3 * u * u * t * c1x + 3 * u * t * t * c2x + t * t * t * x1;
  const y = u * u * u * y0 + 3 * u * u * t * c1y + 3 * u * t * t * c2y + t * t * t * y1;
  const tx = 3 * u * u * (c1x - x0) + 6 * u * t * (c2x - c1x) + 3 * t * t * (x1 - c2x);
  const ty = 3 * u * u * (c1y - y0) + 6 * u * t * (c2y - c1y) + 3 * t * t * (y1 - c2y);
  return { x: r2(x), y: r2(y), deg: deg(tx, ty) };
}

/**
 * زاویه‌ی یک بردار، به درجه.
 *
 * ⚠ بردارِ صفر (دو سرِ یال دقیقاً روی هم) صفر درجه می‌گیرد و نه `NaN`:
 * `atan2(0,0)` خودش صفر می‌دهد ولی مماسِ صفر یعنی «جهتی در کار نیست»، و
 * `NaN` داخلِ `transform`ِ SVG کلِ پیکان را ناپیدا می‌کند.
 */
function deg(dx: number, dy: number): number {
  if (!dx && !dy) return 0;
  return r2((Math.atan2(dy, dx) * 180) / Math.PI);
}

const r2 = (n: number): number => Math.round(n * 100) / 100;

/* ─────────────────────────── رنگِ نرخِ خروج ─────────────────────────── */

/**
 * رنگِ گره از نرخِ خروجش — سبز (هیچ‌کس نرفت) تا قرمز (همه رفتند)، با خنثی
 * وسط.
 *
 * ⚠ خروجی `color-mix` است و نه یک کدِ رنگ: رنگِ ثابت در `apps/admin` ممنوع
 * است (قاعده‌ی CLAUDE.md و گاردِ `guard:css`) و این‌طور هر سه سرِ طیف
 * توکنِ واقعیِ تم‌اند — یعنی در تمِ تیره هم درست می‌مانند.
 *
 * ⚠ «وسط» `--surface` است، یعنی همان زمینه‌ی کارت. گرهِ پنجاه‌درصدی پس
 * عملاً بی‌رنگ می‌شود و فقط با دورِ `--line` دیده می‌شود — که همان چیزی است
 * که خواسته شده: سفیدِ وسطِ طیف.
 */
export function exitColor(rate: number | null): string {
  if (rate === null) return 'var(--brand)';
  const r = Math.min(1, Math.max(0, rate));
  if (r <= 0.5) {
    return `color-mix(in srgb, var(--accent) ${pct(1 - r * 2)}%, var(--surface))`;
  }
  return `color-mix(in srgb, var(--danger) ${pct(r * 2 - 1)}%, var(--surface))`;
}

const pct = (x: number): number => Math.round(x * 100);
